import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["video", "canvas", "preview", "previewImage", "fileInput",
                     "captureArea", "captureBtn", "retakeBtn", "startBtn",
                     "placeholder", "errorMessage"]

  connect() {
    this.stream = null
  }

  disconnect() {
    this.stopCamera()
  }

  async startCamera() {
    this.clearError()

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      })

      this.videoTarget.srcObject = this.stream
      this.videoTarget.play()

      this.placeholderTarget.classList.add("hidden")
      this.captureAreaTarget.classList.remove("hidden")
      this.captureBtnTarget.classList.remove("hidden")

      if (this.hasStartBtnTarget) {
        this.startBtnTarget.classList.add("hidden")
      }
    } catch (err) {
      this.showError(this.cameraErrorMessage(err))
    }
  }

  capture() {
    const video = this.videoTarget
    const canvas = this.canvasTarget

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const ctx = canvas.getContext("2d")
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob((blob) => {
      if (!blob) {
        this.showError("Failed to capture photo. Please try again.")
        return
      }

      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" })
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      this.fileInputTarget.files = dataTransfer.files

      const url = URL.createObjectURL(blob)
      this.previewImageTarget.src = url

      this.stopCamera()

      this.captureAreaTarget.classList.add("hidden")
      this.captureBtnTarget.classList.add("hidden")
      this.previewTarget.classList.remove("hidden")
      this.retakeBtnTarget.classList.remove("hidden")
    }, "image/jpeg", 0.9)
  }

  retake() {
    this.clearError()
    this.previewTarget.classList.add("hidden")
    this.retakeBtnTarget.classList.add("hidden")
    this.fileInputTarget.value = ""

    this.startCamera()
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.hasVideoTarget) {
      this.videoTarget.srcObject = null
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  cameraErrorMessage(err) {
    if (err.name === "NotAllowedError") {
      return "Camera access was denied. Please allow camera permissions in your browser settings and try again."
    }
    if (err.name === "NotFoundError") {
      return "No camera found on this device. Please use a device with a camera."
    }
    if (err.name === "NotReadableError") {
      return "Camera is in use by another application. Please close other apps using the camera."
    }
    return "Could not access camera. Please check your browser permissions and try again."
  }
}
